/**
 * Shield XMLHttpRequest event delivery from Cookiebot's auto-blocking mode.
 * Ticket DCM001CMG01-17401 (frozen order screen).
 *
 * Root cause this works around: `uc.js` in auto-blocking mode
 * (`data-blockingmode="auto"`, hardcoded in @cemex/cmx-cookiebot-v7) replaces
 * `EventTarget.prototype.addEventListener` while consent is being resolved,
 * and QUEUES instead of registering any 'load' / 'readystatechange' listener —
 * on every EventTarget, XHRs included. Zone.js registers its internal
 * 'readystatechange' listener (the one that delivers ALL XHR results to
 * Angular) through exactly that call, so any HttpClient request sent inside
 * that window completes on the network but never reaches JS: the app boots
 * deaf and hangs forever. Replaying the queue later doesn't help — the events
 * already fired. `fetch`, beacons and timers don't use addEventListener,
 * which is why only XHR died. (Confirmed in dev2 HARs: request 200-complete
 * at network level, zero callbacks, probe XHR never reaching readyState 1.)
 *
 * The workaround: XMLHttpRequest has no OWN `addEventListener` — it inherits
 * EventTarget's. Copying the current (Zone-patched: polyfills run before this
 * file) implementation onto the XHR prototypes as an OWN property means the
 * lookup for XHR instances stops there and never reaches whatever Cookiebot
 * later installs on `EventTarget.prototype`. Script/iframe blocking — the
 * actual compliance feature — is untouched: an XHR listener sets no cookies,
 * queuing it was collateral damage of uc.js filtering by event TYPE only
 * (there is no whitelist hook in that code path; verified against uc.js).
 *
 * Must execute BEFORE Cookiebot loads. uc.js is injected by an
 * APP_INITIALIZER (CmxCookiebotModule.forRoot in app.module), so importing
 * this module at the top of main.ts is early enough — and importing it after
 * zone.js (polyfills) guarantees we capture Zone's patched version, keeping
 * NgZone change detection intact.
 *
 * Remove when the underlying conflict is fixed (Cookiebot uc.js update or
 * cmx-cookiebot-v7 switching/making configurable the blocking mode).
 */
(() => {
  try {
    const shield = (proto: { addEventListener?: unknown; removeEventListener?: unknown } | undefined) => {
      if (!proto || Object.prototype.hasOwnProperty.call(proto, 'addEventListener')) {
        return;
      }
      // add/remove must stay a matched pair so Zone can unregister what it registered.
      proto.addEventListener = EventTarget.prototype.addEventListener;
      proto.removeEventListener = EventTarget.prototype.removeEventListener;
    };

    shield(XMLHttpRequest.prototype);
    // Upload progress events arrive through a sibling prototype chain.
    if (typeof XMLHttpRequestUpload !== 'undefined') {
      shield(XMLHttpRequestUpload.prototype);
    }

    // Read back by the boot-trace `env:xhr` beacon so every HAR states on its
    // own whether this shield was active for that boot.
    (window as unknown as { __xhrCookiebotShim?: boolean }).__xhrCookiebotShim = true;
  } catch {
    // A failed shield must never break the boot; the beacon simply won't
    // report it and the capture will tell us.
  }
})();

export {};
